import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ==================== AUTH ====================
export const login = async (username, password) => {
  const response = await axios.post(`${API_URL}/api/auth/login`, {
    username,
    password
  });
  return response.data;
};

export const getMe = async () => {
  const response = await axios.get(`${API_URL}/api/auth/me`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await axios.post(
    `${API_URL}/api/auth/change-password`,
    { current_password: currentPassword, new_password: newPassword },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// ==================== DEPARTMENTS ====================
export const getDepartments = async () => {
  const response = await axios.get(`${API_URL}/api/departments`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDepartment = async (id) => {
  const response = await axios.get(`${API_URL}/api/departments/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createDepartment = async (data) => {
  const response = await axios.post(`${API_URL}/api/departments`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateDepartment = async (id, data) => {
  const response = await axios.put(`${API_URL}/api/departments/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteDepartment = async (id) => {
  const response = await axios.delete(`${API_URL}/api/departments/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const setDepartmentHead = async (deptId, userId) => {
  const response = await axios.put(
    `${API_URL}/api/departments/${deptId}/head`,
    { user_id: userId },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const setDepartmentDeputies = async (deptId, userIds) => {
  const response = await axios.put(
    `${API_URL}/api/departments/${deptId}/deputies`,
    { user_ids: userIds },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// ==================== USERS ====================
export const getUsers = async (departmentId = null) => {
  const params = departmentId ? { department_id: departmentId } : {};
  const response = await axios.get(`${API_URL}/api/users`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const createUser = async (data) => {
  const response = await axios.post(`${API_URL}/api/users`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateUser = async (id, data) => {
  const response = await axios.put(`${API_URL}/api/users/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await axios.delete(`${API_URL}/api/users/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const resetUserPassword = async (id) => {
  const response = await axios.put(
    `${API_URL}/api/users/${id}/reset-password`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

// ==================== FORMS ====================
export const getForms = async (status = null, departmentId = null) => {
  const params = {};
  if (status) params.status = status;
  if (departmentId) params.department_id = departmentId;
  
  const response = await axios.get(`${API_URL}/api/forms`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const getFormsPendingApproval = async () => {
  const response = await axios.get(`${API_URL}/api/forms/pending-approval`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getForm = async (id) => {
  const response = await axios.get(`${API_URL}/api/forms/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getPublicForm = async (slug, password = null) => {
  const params = password ? { password } : {};
  const response = await axios.get(`${API_URL}/api/forms/public/${slug}`, { params });
  return response.data;
};

export const createForm = async (data) => {
  const response = await axios.post(`${API_URL}/api/forms`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateForm = async (id, data) => {
  const response = await axios.put(`${API_URL}/api/forms/${id}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteForm = async (id) => {
  const response = await axios.delete(`${API_URL}/api/forms/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const submitFormForApproval = async (id, comment = null) => {
  const response = await axios.post(
    `${API_URL}/api/forms/${id}/submit-for-approval`,
    { comment },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const approveForm = async (id, comment = null) => {
  const response = await axios.post(
    `${API_URL}/api/forms/${id}/approve`,
    { action: 'approve', comment },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const rejectForm = async (id, comment) => {
  const response = await axios.post(
    `${API_URL}/api/forms/${id}/approve`,
    { action: 'reject', comment },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const publishForm = async (id) => {
  const response = await axios.post(
    `${API_URL}/api/forms/${id}/publish`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const uploadFormLogo = async (formId, file) => {
  const formData = new FormData();
  formData.append('logo', file);
  
  const response = await axios.post(
    `${API_URL}/api/forms/${formId}/logo`,
    formData,
    {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// ==================== SUBMISSIONS ====================
export const getSubmissions = async (formId = null, page = 1, perPage = 50) => {
  const params = { page, per_page: perPage };
  if (formId) params.form_id = formId;
  
  const response = await axios.get(`${API_URL}/api/submissions`, {
    headers: getAuthHeader(),
    params
  });
  return response.data;
};

export const getSubmission = async (id) => {
  const response = await axios.get(`${API_URL}/api/submissions/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createSubmission = async (formId, data, password = null) => {
  const response = await axios.post(
    `${API_URL}/api/submissions`,
    { form_id: formId, data, password }
  );
  return response.data;
};

export const exportSubmissions = async (formId = null, departmentId = null, dateFrom = null, dateTo = null, format = 'csv') => {
  const params = new URLSearchParams();
  if (formId) params.append('form_id', formId);
  if (departmentId) params.append('department_id', departmentId);
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  
  const endpoint = format === 'xlsx' ? 'xlsx' : 'csv';
  const mimeType = format === 'xlsx' 
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'text/csv';
  
  const response = await axios.get(
    `${API_URL}/api/submissions/export/${endpoint}?${params.toString()}`,
    {
      headers: getAuthHeader(),
      responseType: 'blob'
    }
  );
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `export_${new Date().toISOString().slice(0,10)}.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return { success: true };
};

export const deleteSubmission = async (id) => {
  const response = await axios.delete(`${API_URL}/api/submissions/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ==================== FILE UPLOADS ====================
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API_URL}/api/uploads/file`, formData, {
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const getFileUrl = (filename) => {
  return `${API_URL}/api/uploads/submissions/${filename}`;
};

// ==================== GLOBAL DROPDOWNS ====================
export const getGlobalDropdowns = async () => {
  const response = await axios.get(`${API_URL}/api/global-dropdowns`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getGlobalDropdown = async (id) => {
  const response = await axios.get(`${API_URL}/api/global-dropdowns/${id}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getGlobalDropdownByName = async (name) => {
  const response = await axios.get(`${API_URL}/api/global-dropdowns/by-name/${name}`);
  return response.data;
};

export const getRegions = async () => {
  const response = await axios.get(`${API_URL}/api/settings/regions`);
  return response.data;
};

// ==================== SETTINGS ====================
export const getSettings = async () => {
  const response = await axios.get(`${API_URL}/api/settings`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateSettings = async (data) => {
  const response = await axios.put(`${API_URL}/api/settings`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ==================== STATISTICS ====================
export const getGlobalStats = async (timeRange = 'week') => {
  const response = await axios.get(`${API_URL}/api/stats/global`, {
    headers: getAuthHeader(),
    params: { time_range: timeRange }
  });
  return response.data;
};

export const getDepartmentStats = async (timeRange = 'week') => {
  const response = await axios.get(`${API_URL}/api/stats/departments`, {
    headers: getAuthHeader(),
    params: { time_range: timeRange }
  });
  return response.data;
};


// ==================== BRANDING ====================
export const getBranding = async () => {
  const response = await axios.get(`${API_URL}/api/settings/branding`);
  return response.data;
};

export const updateBranding = async (data) => {
  const response = await axios.put(`${API_URL}/api/settings/branding`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const uploadBrandingLogo = async (file, logoType) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("logo_type", logoType);
  
  const response = await axios.post(`${API_URL}/api/settings/branding/logo`, formData, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }
  });
  return response.data;
};

// ==================== SYSTEM TEXTS ====================
export const getSystemTexts = async () => {
  const response = await axios.get(`${API_URL}/api/settings/texts`);
  return response.data;
};

export const updateSystemTexts = async (data) => {
  const response = await axios.put(`${API_URL}/api/settings/texts`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

